
import React from 'react';
import { useDispatch, useSelector  } from 'react-redux';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { mergeStyleSets } from 'office-ui-fabric-react/lib/Styling';
import GeoJSON from 'ol/format/GeoJSON';
import get from 'lodash/get';

import {
    DialogConfirm,
    IfComp,
    ToolbarCommandButton
} from '../../../core';

import {
    sagas as sagasMap,
    getTempFeature
} from '../../../map';

import {
    getDataProjectId,
    getMapLayerByAttachedDataModel,
    useToken
} from '../../../project';

import { getActions } from '../../sagas';

import {
    getInfo,
    getDataModelDefinition
} from '../../reducers'

import {
    INFO_TYPE_HOME,
} from '../../reducers/infoPanelType';

import {
    getObjectCreate,
    getObjectCreateKey,
    getObjectCreateChanged
} from '../../reducers/info'

import { getDataModelItem } from '../../reducers/dataModelDefinition'

import { useDataModelOperationInsert } from '../../hooks/useDataModelOperation';

import ObjectEditor from '../objectEdit/ObjectEditor';
import ObjectValidationResult from '../ObjectValidationResult';

const ObjectCreatePanel = ({appActions}) => {

    const classNames = mergeStyleSets({
        ObjectCreatePanel: {
        },
        content: {
            overflowY: 'auto',
            height: 0
        }
    });

    const dispatch = useDispatch();
    const dataActions = getActions(dispatch);
    const mapActions = sagasMap.getActions(dispatch);

    const dataProjectId = useSelector(state => getDataProjectId(state.project));

    const objectCreate = useSelector(state => getObjectCreate(getInfo(state.data)));
    const objectCreateKey = useSelector(state => getObjectCreateKey(getInfo(state.data)));
    const objectCreateChanged = useSelector(state => getObjectCreateChanged(getInfo(state.data)));
    const dataModelItem = useSelector(state => getDataModelItem(getDataModelDefinition(state.data), objectCreate.dataModelId));
    const project = useSelector(state => state.project);
    const mapLayer = getMapLayerByAttachedDataModel(project, objectCreate.dataModelId);
    const tempFeature = useSelector(state => getTempFeature(state.map.map));
    const operationInsert = useDataModelOperationInsert(dataModelItem);
    const token = useToken();

    React.useEffect(() => {
        if (dataModelItem) {
            switch (get(dataModelItem, 'geoData.geometryType')) {
                case 'LINE':
                    mapActions.setToolsMode('INPUT_LINE');
                    break;
                case 'POINT':
                    mapActions.setToolsMode('INPUT_POINT');
                    break;
                case 'POLYGON':
                    mapActions.setToolsMode('INPUT_POLYGON');
                    break;
            }
        }

        return () => {
            mapActions.setToolsMode('DEFAULT');
            mapActions.addCommand('SET_TEMP_FEATURE', {features: []});
            mapActions.setTempFeature();
        };

    }, [objectCreateKey]);

    React.useEffect(() => {
        if (tempFeature) {
            let format = new GeoJSON();
            let geoJson = format.writeGeometry(tempFeature.getGeometry());
            dataActions.setObjectCreateAttrValue('_geom', geoJson);
        }
    }, [tempFeature]);

    const handleBackClick = async () => {
        const canBack = (objectCreateChanged.length > 0)
            ? await DialogConfirm.show(`Opravdu chcete odejít zpět bez uložení změn ?`)
            : true;
        if (canBack) {
            dataActions.setType(INFO_TYPE_HOME);
        }
    }

    const handleChangeEditor = (attrName, value) => {
        dataActions.setObjectCreateAttrValue(attrName, value);
    }

    const handleCreateClick = () => {
        const attrNames = [
            ...objectCreateChanged,
            'gid'
        ];

        dataActions.insertObject(
            token, dataProjectId, dataModelItem.id, mapLayer.id,
            {
                attributes: Object.keys(objectCreate.attributes).reduce((previous, current) => {
                    if (attrNames.indexOf(current) !== -1) {
                        previous[current] = objectCreate.attributes[current];
                    }
                    return previous;
                }, {})
            }
        );
    }

    const handleValidate = () => {
        let errors = [];

        if (!tempFeature) {
            errors.push(`Geometrie není zadaná, použijte v mapě nástroj pro její zakreslení`);
        }

        dataModelItem.attributes.forEach(item => {
            if (item.required) {
                if (objectCreateChanged.indexOf(item.name) === -1) {
                    errors.push(`Atribut "${item.title}" nemá zadanou hodnotu`);
                }
            }
        });

        return errors;
    }

    let commands = [];

    commands.push({
        text: 'Vytvořit',
        title: 'Vytvořit nový objekt',
        disabled: (handleValidate().length > 0),
        onClick: handleCreateClick
    });
    commands.push({
        text: 'Zpět',
        title: 'Zpět bez uložení změn',
        onClick: handleBackClick
    })

    return (
        <Stack verticalFill className={classNames.ObjectCreatePanel} tokens={{childrenGap: 8}}>
            <IfComp cond={dataModelItem}>
                <Stack.Item grow className={classNames.content}>
                    <ObjectEditor
                        dataModel={dataModelItem}
                        object={objectCreate}
                        onChange={handleChangeEditor}
                        mode="insert"
                    />
                </Stack.Item>
            </IfComp>
            <Stack.Item>
                <ObjectValidationResult
                    items={handleValidate()}
                    detailed={true}
                />
            </Stack.Item>
            <ToolbarCommandButton
                items={commands}
            />
        </Stack>
    );
}

export default ObjectCreatePanel;
